/**
 * What a staff upload of a dossier file must meet before it is sent: the document type's own rule
 * (extensions, maximum size) and, for a type that goes to VNNIC, VNNIC's file rule (HSTM Phụ lục 1
 * Bảng 3: file kinds, minimum and maximum size; `vnnic_format` of
 * `GET /dossier-queue/{id}/upload-types`). The server checks both again; checking here keeps a
 * file VNNIC would refuse (a PDF business registration, a 70-byte photo) from being sent at all.
 */

export interface VnnicFileFormat {
  mime_types: string[];
  min_bytes?: number | null;
  max_bytes: number;
  min_pixels?: number | null;
  max_pixels?: number | null;
}

export interface UploadType {
  allowed_extensions: string[];
  max_bytes: number;
  vnnic_format?: VnnicFileFormat | null;
}

const MIME: Record<string, string> = { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' };

/** The extensions a file of this type may have: the type's, narrowed to what VNNIC takes. */
export function uploadExtensions(type: UploadType): string[] {
  const vnnic = type.vnnic_format?.mime_types;
  if (!vnnic?.length) return type.allowed_extensions;
  const both = type.allowed_extensions.filter((e) => vnnic.includes(MIME[e.toLowerCase()] ?? ''));
  return both.length ? both : type.allowed_extensions;
}

export type UploadProblem = { code: 'extension'; list: string[] } | { code: 'tooLarge'; max: number } | { code: 'tooSmall'; min: number };

/** Why this file cannot be uploaded as this type, or null. */
export function uploadProblem(type: UploadType, file: { name: string; size: number }): UploadProblem | null {
  const ext = (file.name.split('.').pop() ?? '').toLowerCase();
  const list = uploadExtensions(type);
  if (!list.includes(ext)) return { code: 'extension', list };
  const max = Math.min(type.max_bytes, type.vnnic_format?.max_bytes ?? Number.POSITIVE_INFINITY);
  if (file.size > max) return { code: 'tooLarge', max };
  const min = type.vnnic_format?.min_bytes ?? 0;
  if (min && file.size < min) return { code: 'tooSmall', min };
  return null;
}
